import { useMemo, useState } from "react";
import {
  Bar, CartesianGrid, Cell, ComposedChart, Line, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  ArrowDownRight, ArrowUpRight, Music4, ShoppingBag, UserPlus, Wallet,
} from "lucide-react";
import { localDayKey, kyivHour, prevDayKey, orderSource, fmtDateShort, fmtTimeShort, plural, fmtMoney } from "./utils";

const BLUE = "#2498FF";
const PURPLE = "#7C4DFF";
const CYAN = "#18C8FF";
const PINK = "#C44DFF";
const GREEN = "#31D58A";
const AMBER = "#FFB84D";
const RED = "#FF5C7A";

const GENRE_LABELS = {
  pop: "Поп", rock: "Рок", rap: "Реп", chanson: "Шансон", disney: "Disney",
  guitar: "Гітара", dance: "Танці", folk: "Український фолк", disco: "Диско",
  romantic: "Романтична", jazz: "Джаз", lullaby: "Колискова",
};

const SOURCE_META = {
  site1: { label: "Конструктор 1", color: BLUE },
  site2: { label: "Конструктор 2", color: PURPLE },
  site: { label: "Сайт", color: CYAN },
  tg: { label: "Telegram-бот", color: PINK },
  tgs: { label: "Бот · простий", color: "#8B5CF6" },
  admin: { label: "Адмін", color: "#64748B" },
  other: { label: "Інше", color: "#475569" },
};

const ORDER_BADGE = {
  done: ["Завершено", "#31D58A"],
  paid: ["Оплачено", "#31D58A"],
  in_progress: ["Генерується", "#60A5FA"],
  pending: ["На перевірці", AMBER],
  screenshot_wait: ["Чекає скрін", AMBER],
  unpaid: ["Без оплати", "#FFD83D"],
  created: ["Створено", "#94A3BD"],
  expired: ["Прострочено", RED],
  rejected: ["Відхилено", RED],
};

const MONTHS_UK = ["Січ", "Лют", "Бер", "Кві", "Тра", "Чер", "Лип", "Сер", "Вер", "Жов", "Лис", "Гру"];

const RANGES = [
  { id: "day", label: "День", short: "Д" },
  { id: "week", label: "Тиждень", short: "Т" },
  { id: "month", label: "Місяць", short: "М" },
  { id: "year", label: "Рік", short: "Р" },
];

function buildSeries(range, orders) {
  const now = new Date();
  const points = [];
  let match;
  if (range === "day") {
    for (let h = 0; h < 24; h += 1) {
      points.push({ label: `${String(h).padStart(2, "0")}:00`, hour: h, orders: 0, revenue: 0 });
    }
    const today = localDayKey(now);
    match = (d) => (localDayKey(d) === today ? kyivHour(d) : -1);
  } else if (range === "year") {
    for (let i = 11; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      points.push({ label: MONTHS_UK[d.getMonth()], key: `${d.getFullYear()}-${d.getMonth()}`, orders: 0, revenue: 0 });
    }
    match = (d) => `${d.getFullYear()}-${d.getMonth()}`;
  } else {
    const n = range === "week" ? 7 : 30;
    for (let i = n - 1; i >= 0; i -= 1) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      points.push({
        label: `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`,
        key: localDayKey(d), orders: 0, revenue: 0,
      });
    }
    match = (d) => localDayKey(d);
  }
  (orders || []).forEach((o) => {
    if (["unpaid", "expired", "rejected"].includes(o.status)) return;
    const d = new Date(o.created_at);
    if (isNaN(d.getTime())) return;
    const k = match(d);
    const p = points.find((x) => (x.key !== undefined ? x.key === k : x.hour === k));
    if (!p) return;
    p.orders += 1;
    p.revenue += Number(o.amount || 0);
  });
  return points;
}

function deltaPct(current, previous) {
  if (!previous) return null;
  return Math.round(((current - previous) / previous) * 100);
}

function DeltaChip({ delta, muted }) {
  if (muted) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold text-[#64748B] bg-white/[0.04] border border-white/[0.06]">
        за весь час
      </span>
    );
  }
  if (delta == null) {
    return <span className="text-[11px] font-semibold text-[#64748B]">—</span>;
  }
  const up = delta >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-1 text-[11px] font-semibold ${up ? "text-[#31D58A] bg-[#31D58A]/10" : "text-[#FF5C7A] bg-[#FF5C7A]/10"}`}>
      {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      {up ? "+" : ""}{delta}%
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, sub, delta, muted, color, tint, testid, loading }) {
  return (
    <div data-testid={testid} className={`adm-card adm-card-hover rounded-2xl p-4 sm:p-5 ${tint}`}>
      <div className="flex items-center gap-3 sm:hidden">
        <span className="grid place-items-center w-11 h-11 rounded-xl shrink-0" style={{ background: `${color}1f`, color }}>
          <Icon size={20} strokeWidth={1.9} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[12px] text-[#94A3BD]">{label}</div>
          <div className="text-2xl font-bold leading-tight text-white mt-0.5">{loading ? "…" : value}</div>
          <div className="text-[11px] text-[#64748B] mt-0.5 truncate">{sub}</div>
        </div>
        <DeltaChip delta={delta} muted={muted} />
      </div>
      <div className="hidden sm:block">
        <div className="flex items-start justify-between">
          <span className="grid place-items-center w-12 h-12 rounded-xl" style={{ background: `${color}1f`, color }}>
            <Icon size={22} strokeWidth={1.9} />
          </span>
          <DeltaChip delta={delta} muted={muted} />
        </div>
        <div className="mt-4 text-[30px] font-bold leading-none tracking-tight text-white">{loading ? "…" : value}</div>
        <div className="text-[13px] text-[#94A3BD] mt-1.5">{label}</div>
        <div className="text-[11px] text-[#64748B] mt-1">{sub}</div>
      </div>
    </div>
  );
}

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-white/10 px-3 py-2 text-xs" style={{ background: "rgba(13,25,43,0.96)" }}>
      <div className="text-[#94A3BD] mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.dataKey === "revenue" ? PURPLE : BLUE }} />
          <span className="text-[#94A3BD]">{p.name}:</span>
          <span className="font-semibold text-white">
            {p.dataKey === "revenue" ? `${Math.round(p.value)} ₴` : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

function CardHead({ title, legend, action, children }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 className="text-[17px] font-semibold text-white">{title}</h3>
        {legend}
      </div>
      {action || children}
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-[#94A3BD]">
      <span className="w-2 h-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

function RevenueCard({ orders, loading }) {
  const [range, setRange] = useState("month");
  const series = useMemo(() => buildSeries(range, orders || []), [range, orders]);
  const hasData = series.some((p) => p.revenue > 0 || p.orders > 0);
  return (
    <div className="adm-card rounded-2xl p-5 xl:col-span-2">
      <CardHead
        title="Доход і замовлення"
        legend={
          <div className="flex items-center gap-3 mt-2">
            <LegendDot color={PURPLE} label="Дохід" />
            <LegendDot color={BLUE} label="Замовлення" />
          </div>
        }
        action={
          <div className="flex items-center gap-1 rounded-xl border border-white/[0.07] bg-[#0B1729] p-1">
            {RANGES.map((r) => (
              <button key={r.id} onClick={() => setRange(r.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${range === r.id ? "bg-[#17264A] text-white" : "text-[#64748B] hover:text-white"}`}>
                <span className="hidden sm:inline">{r.label}</span>
                <span className="sm:hidden">{r.short}</span>
              </button>
            ))}
          </div>
        }
      />
      <div className="h-[290px] mt-5 relative">
        {loading && !orders?.length ? (
          <div className="absolute inset-0 grid place-items-center text-sm text-[#64748B]">Завантаження…</div>
        ) : hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={series} margin={{ top: 6, right: 6, left: -14, bottom: 0 }}>
              <defs>
                <linearGradient id="admRevGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.95} />
                  <stop offset="100%" stopColor="#6C3DFF" stopOpacity={0.55} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} minTickGap={22}
                tick={{ fill: "#64748B", fontSize: 11 }} />
              <YAxis tickLine={false} axisLine={false} width={44}
                tick={{ fill: "#64748B", fontSize: 11 }} />
              <Tooltip cursor={{ fill: "rgba(255,255,255,0.03)" }} content={<ChartTip />} />
              <Bar dataKey="revenue" name="Дохід" fill="url(#admRevGrad)" radius={[6, 6, 0, 0]} maxBarSize={26} />
              <Line type="monotone" dataKey="orders" name="Замовлення" stroke={BLUE} strokeWidth={2.2}
                dot={false} activeDot={{ r: 4, fill: BLUE, strokeWidth: 0 }} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-center text-sm text-[#64748B]">Ще немає даних за цей період</div>
          </div>
        )}
      </div>
    </div>
  );
}

function TrafficCard({ orders }) {
  const data = useMemo(() => {
    const counts = {};
    (orders || []).forEach((o) => {
      const k = orderSource(o);
      counts[k] = (counts[k] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([key, value]) => ({
        key, value,
        name: SOURCE_META[key]?.label || key,
        color: SOURCE_META[key]?.color || "#475569",
      }))
      .sort((a, b) => b.value - a.value);
  }, [orders]);
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className="adm-card rounded-2xl p-5">
      <CardHead title="Джерела трафіку" legend={<div className="text-[11px] text-[#64748B] mt-1">Звідки приходять замовлення</div>} />
      {total ? (
        <>
          <div className="relative h-[186px] mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" innerRadius={58} outerRadius={82} paddingAngle={3} stroke="none">
                  {data.map((d) => <Cell key={d.key} fill={d.color} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="text-center">
                <div className="text-2xl font-bold text-white leading-none">{total}</div>
                <div className="text-[11px] text-[#64748B] mt-1">замовлень</div>
              </div>
            </div>
          </div>
          <div className="mt-4 space-y-2.5">
            {data.map((d) => (
              <div key={d.key} className="flex items-center gap-2 text-[13px]">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.color }} />
                <span className="text-[#CBD5E1] truncate">{d.name}</span>
                <span className="ml-auto text-[#64748B] font-mono text-[11px]">{d.value}</span>
                <span className="text-white font-semibold w-10 text-right">{Math.round((d.value / total) * 100)}%</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="mt-4">
          <div className="relative h-[186px] grid place-items-center">
            <span className="w-[150px] h-[150px] rounded-full border-[14px] border-white/[0.05]" />
            <span className="absolute text-center">
              <span className="block text-2xl font-bold text-[#475569]">0</span>
              <span className="block text-[11px] text-[#64748B] mt-1">замовлень</span>
            </span>
          </div>
          <div className="text-center text-xs text-[#64748B]">Дані з'являться після перших замовлень</div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const [label, color] = ORDER_BADGE[status] || [status || "—", "#94A3BD"];
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium whitespace-nowrap"
      style={{ background: `${color}1a`, color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

function userLabel(o) {
  if (o.username) return `@${o.username}`;
  if (o.landing_contact) return o.landing_contact;
  if (o.user_telegram_id) return `TG ${o.user_telegram_id}`;
  return "—";
}

function tariffLabel(o) {
  if (o.songs) return `${o.songs} ${plural(o.songs, "пісня", "пісні", "пісень")}`;
  const name = (o.package_name || "").replace(/^[^\p{L}\p{N}]+/u, "").trim();
  return name || "—";
}

function RecentOrdersCard({ orders, onTab }) {
  const rows = useMemo(() => [...(orders || [])]
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
    .slice(0, 6), [orders]);
  const today = localDayKey(new Date());
  return (
    <div className="adm-card rounded-2xl p-5 xl:col-span-2">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-[17px] font-semibold text-white">Останні замовлення</h3>
        <button onClick={() => onTab("payments")} className="text-xs font-semibold text-[#2498FF] hover:text-[#7CC5FF] transition-colors">
          Всі →
        </button>
      </div>

      {rows.length ? (
        <>
          <div className="hidden md:block overflow-x-auto mt-4">
            <table className="w-full">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#64748B]">
                  <th className="py-2 pr-2 font-medium">#</th>
                  <th className="py-2 pr-2 font-medium">Користувач</th>
                  <th className="py-2 pr-2 font-medium">Подія / Опис</th>
                  <th className="py-2 pr-2 font-medium">Тариф</th>
                  <th className="py-2 pr-2 font-medium">Статус</th>
                  <th className="py-2 pr-2 font-medium text-right">Сума</th>
                  <th className="py-2 font-medium text-right">Дата</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o._id} className="border-t border-white/[0.05] text-[13px]">
                    <td className="py-3 pr-2 font-mono text-[#94A3BD]">#{o.number || "—"}</td>
                    <td className="py-3 pr-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-7 h-7 rounded-full adm-grad grid place-items-center text-[10px] font-bold text-white shrink-0">
                          {(userLabel(o).replace("@", "")[0] || "#").toUpperCase()}
                        </span>
                        <span className="text-white font-medium truncate max-w-[104px]">{userLabel(o)}</span>
                      </div>
                    </td>
                    <td className="py-3 pr-2 text-[#94A3BD] truncate max-w-[150px]">
                      {o.landing_description || o.landing_event_label || o.landing_genre_label || "—"}
                    </td>
                    <td className="py-3 pr-2 text-[#94A3BD] whitespace-nowrap">{tariffLabel(o)}</td>
                    <td className="py-3 pr-2"><StatusBadge status={o.status} /></td>
                    <td className="py-3 pr-2 text-right font-semibold text-white whitespace-nowrap">
                      {(o.amount || 0) ? fmtMoney(o.amount) : "—"}
                    </td>
                    <td className="py-3 text-right text-[#64748B] font-mono text-[11px] whitespace-nowrap">
                      {localDayKey(o.created_at) === today ? fmtTimeShort(o.created_at) : fmtDateShort(o.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden mt-4 space-y-3">
            {rows.map((o) => (
              <div key={o._id} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3.5">
                <div className="flex items-center gap-2.5">
                  <span className="w-9 h-9 rounded-full adm-grad grid place-items-center text-[11px] font-bold text-white shrink-0">
                    {(userLabel(o).replace("@", "")[0] || "#").toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-white truncate">{userLabel(o)}</div>
                    <div className="text-[11px] text-[#64748B] truncate">
                      {o.landing_description || o.landing_event_label || o.landing_genre_label || "—"}
                    </div>
                  </div>
                  <span className="font-mono text-[11px] text-[#64748B]">#{o.number || "—"}</span>
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <span className="text-[11px] text-[#94A3BD]">{tariffLabel(o)}</span>
                  <span className="text-sm font-bold text-white">{(o.amount || 0) ? fmtMoney(o.amount) : "—"}</span>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <StatusBadge status={o.status} />
                  <span className="text-[11px] text-[#64748B] font-mono">
                    {localDayKey(o.created_at) === today ? fmtTimeShort(o.created_at) : fmtDateShort(o.created_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="py-12 text-center text-sm text-[#64748B]">Замовлень ще немає</div>
      )}
    </div>
  );
}

function GenresCard({ stats }) {
  const rows = useMemo(() => {
    const list = [...(stats?.genres || [])].sort((a, b) => b.value - a.value).slice(0, 5);
    const fallback = ["pop", "rap", "chanson", "dance", "rock"];
    while (list.length < 5) list.push({ name: fallback[list.length], value: 0, empty: true });
    const total = (stats?.genres || []).reduce((s, g) => s + (g.value || 0), 0) || 1;
    return list.map((g) => ({
      ...g,
      label: GENRE_LABELS[g.name] || g.name,
      pct: Math.round(((g.value || 0) / total) * 100),
    }));
  }, [stats]);
  return (
    <div className="adm-card rounded-2xl p-5">
      <CardHead title="Популярні жанри" legend={<div className="text-[11px] text-[#64748B] mt-1">Топ-5 за кількістю пісень</div>} />
      <div className="mt-4 space-y-4">
        {rows.map((g, i) => (
          <div key={g.name + i}>
            <div className="flex items-center gap-2 text-[13px]">
              <span className="text-[11px] font-mono text-[#475569] w-4">{String(i + 1).padStart(2, "0")}</span>
              <span className={`truncate ${g.empty ? "text-[#64748B]" : "text-white"}`}>{g.label}</span>
              <span className="ml-auto font-semibold text-[#94A3BD]">{g.pct}%</span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${g.pct}%`, background: "linear-gradient(90deg,#168CFF,#6C3DFF)" }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GenStatusCard({ songs, orders }) {
  const rows = useMemo(() => {
    const c = { completed: 0, demo: 0, lyrics: 0, error: 0 };
    (songs || []).forEach((s) => {
      if (c[s.status] !== undefined) c[s.status] += 1;
    });
    (orders || []).forEach((o) => {
      (o.gen?.runs || []).forEach((r) => { if (r.error) c.error += 1; });
    });
    const total = c.completed + c.demo + c.lyrics + c.error;
    return [
      { key: "completed", label: "Завершено", color: GREEN, value: c.completed },
      { key: "demo", label: "Генерується", color: "#3B82F6", value: c.demo },
      { key: "lyrics", label: "Очікує", color: AMBER, value: c.lyrics },
      { key: "error", label: "Помилка", color: RED, value: c.error },
    ].map((x) => ({ ...x, pct: total ? Math.round((x.value / total) * 100) : 0, total }));
  }, [songs, orders]);
  const total = rows[0]?.total || 0;
  return (
    <div className="adm-card rounded-2xl p-5">
      <CardHead title="Статуси генерацій" legend={<div className="text-[11px] text-[#64748B] mt-1">Стан усіх пісень</div>} />
      <div className="mt-5 h-2.5 rounded-full overflow-hidden bg-white/[0.05] flex gap-[3px]">
        {total ? rows.map((r) => (
          <div key={r.key} className="h-full first:rounded-l-full last:rounded-r-full"
            style={{ width: `${(r.value / total) * 100}%`, background: r.color, opacity: 0.9 }} />
        )) : <div className="h-full w-full bg-white/[0.04]" />}
      </div>
      <div className="mt-5 space-y-3">
        {rows.map((r) => (
          <div key={r.key} className="flex items-center gap-2 text-[13px]">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: r.color }} />
            <span className="text-[#CBD5E1]">{r.label}</span>
            <span className="ml-auto text-[#64748B] font-mono text-[11px]">{r.value}</span>
            <span className="text-white font-semibold w-10 text-right">{r.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PricingCard({ packages, orders }) {
  const rows = useMemo(() => {
    const list = (packages || []).map((p) => {
      const items = (orders || []).filter((o) => o.package_key === p.key);
      return {
        key: p.key,
        label: `${p.songs_count} ${plural(p.songs_count, "пісня", "пісні", "пісень")}`,
        price: p.price_uah,
        count: items.length,
        revenue: items.reduce((s, o) => s + Number(o.amount || 0), 0),
      };
    });
    const total = list.reduce((s, r) => s + r.count, 0);
    return list.map((r) => ({ ...r, pct: total ? Math.round((r.count / total) * 100) : 0, total }));
  }, [packages, orders]);
  const total = rows[0]?.total || 0;
  return (
    <div className="adm-card rounded-2xl p-5 xl:col-span-2">
      <CardHead title="Тарифна сітка (продажі)" legend={<div className="text-[11px] text-[#64748B] mt-1">Продано пакетів: {total}</div>} />
      <div className="mt-4 space-y-4">
        {rows.length ? rows.map((r) => (
          <div key={r.key}>
            <div className="flex items-center gap-2 text-[13px]">
              <span className="text-white font-medium">{r.label}</span>
              <span className="text-[11px] text-[#64748B]">({Math.round(r.price)} ₴)</span>
              <span className="ml-auto font-semibold text-[#94A3BD]">{r.pct}%</span>
              <span className="text-[11px] font-mono text-[#475569] w-8 text-right">×{r.count}</span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${r.pct}%`, background: "linear-gradient(90deg,#168CFF,#6C3DFF)" }} />
            </div>
          </div>
        )) : (
          <div className="py-8 text-center text-sm text-[#64748B]">Пакети недоступні</div>
        )}
      </div>
    </div>
  );
}

function ExtraMetrics({ stats, money, onModerate, loading }) {
  const k = stats?.kpi || {};
  const items = [
    { testid: "kpi-total-users", label: "Всього користувачів", value: k.total_users ?? 0 },
    { testid: "kpi-total-songs", label: "Всього пісень", value: k.total_songs ?? 0 },
    { testid: "kpi-total-revenue", label: "Виручка за весь час", value: fmtMoney(k.total_revenue ?? 0) },
    { testid: "kpi-conversion", label: "Конверсія", value: `${k.conversion ?? 0}%` },
    { testid: "kpi-fraud", label: "Антифрод", value: k.fraud_flags ?? 0, action: "fraud", hint: "натисніть, щоб переглянути" },
    { testid: "kpi-banned", label: "Забанено", value: k.banned ?? 0, action: "banned", hint: "натисніть, щоб розбанити" },
    { testid: "money-revenue", label: "Дохід за період", value: fmtMoney(money?.revenue ?? 0) },
    { testid: "money-payouts", label: "Виплати партнерам", value: fmtMoney(money?.payouts ?? 0) },
    { testid: "money-gencost", label: "Витрати (генерації)",
      value: fmtMoney(((money?.expenses || {})[money?.period === "today" ? "today" : "all"] || {}).total ?? 0),
      hint: `в т.ч. LLM ${Math.round(((money?.expenses || {})[money?.period === "today" ? "today" : "all"] || {}).llm ?? 0)}₴ + Suno ${Math.round(((money?.expenses || {})[money?.period === "today" ? "today" : "all"] || {}).music ?? 0)}₴` },
    { testid: "money-net", label: "Чистий дохід",
      value: fmtMoney((money?.period === "today" ? money?.net_today : money?.net_all) ?? 0) },
  ];
  return (
    <div className="adm-card rounded-2xl p-5">
      <h3 className="text-[17px] font-semibold text-white">Додаткові метрики</h3>
      <p className="text-[11px] text-[#64748B] mt-1">Повний зріз по користувачах, грошах і модерації</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 mt-4">
        {items.map((it) => (
          <div key={it.testid} data-testid={it.testid}
            onClick={it.action ? () => onModerate(it.action) : undefined}
            className={`rounded-xl border border-white/[0.06] bg-white/[0.02] px-3.5 py-3 ${it.action ? "cursor-pointer hover:border-[#2498FF]/40 transition-colors" : ""}`}>
            <div className="text-[11px] text-[#94A3BD] leading-snug">{it.label}</div>
            <div className="text-lg font-bold text-white mt-1">{loading ? "…" : it.value}</div>
            {it.hint && <div className="text-[10px] text-[#475569] mt-0.5">{it.hint}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

const COST_COLORS = {
  lyrics: "#38BDF8", proofread: "#A855F7", director: "#10B981",
  structure: "#F59E0B", suno: "#EAB308", suno_tags: "#94A3B8",
  genre: "#22D3EE", mood: "#F472B6",
};

function CostsCard({ money, period }) {
  const e = (money?.expenses || {});
  const isToday = period === "today";
  const cur = isToday ? (e.today || {}) : (e.all || {});
  const stages = Object.entries(cur.by_stage || {})
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);
  const total = Number(cur.total || 0);
  const allTotal = Number((e.all || {}).total || 0);
  return (
    <div className="adm-card rounded-2xl p-5" data-testid="costs-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[17px] font-semibold text-white">Витрати</h3>
          <p className="text-[11px] text-[#64748B] mt-1">
            Генерації текстів, режисер, вичитка, Suno — {isToday ? "за сьогодні" : "за весь час"}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[22px] font-bold text-[#F87171] leading-none">{fmtMoney(total)}</div>
          {!isToday && allTotal > 0 && (
            <div className="text-[11px] text-[#64748B] mt-1">за весь час: {fmtMoney(allTotal)}</div>
          )}
        </div>
      </div>
      <div className="mt-4 h-3 rounded-full overflow-hidden bg-white/[0.05] flex gap-[2px]">
        {total > 0 ? stages.map(([k, v]) => (
          <span key={k} className="h-full" title={`${k}: ${fmtMoney(v)}`}
            style={{ width: `${(v / total) * 100}%`, background: COST_COLORS[k] || "#64748B" }} />
        )) : <span className="h-full w-full bg-white/[0.04]" />}
      </div>
      <div className="mt-4 space-y-2">
        {stages.length ? stages.map(([k, v]) => (
          <div key={k} className="flex items-center gap-2 text-[12.5px]">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: COST_COLORS[k] || "#64748B" }} />
            <span className="text-[#CBD5E1]">{COST_STAGE_LABELS[k] || k}</span>
            <span className="ml-auto font-semibold text-[#94A3BD]">{fmtMoney(v)}</span>
            <span className="text-[11px] font-mono text-[#475569] w-10 text-right">
              {total ? Math.round((v / total) * 100) : 0}%
            </span>
          </div>
        )) : <div className="py-6 text-center text-sm text-[#64748B]">Витрат за період немає</div>}
      </div>
    </div>
  );
}

const COST_STAGE_LABELS = {
  lyrics: "Текст (opus)", proofread: "Вичитка (opus)", director: "Режисер (sonnet)",
  structure: "Структура (gemini)", suno: "Suno (рендер)", suno_tags: "Теги (gpt-5-mini)",
  genre: "Розбір стилю", mood: "Розбір настрою",
};

export default function Dashboard({
  stats, orders, users, usersTotal, songs, packages,
  loading, period, setPeriod, onTab, onModerate,
}) {
  const k = stats?.kpi || {};
  const m = stats?.money || {};
  const ordersArr = orders || [];
  const usersArr = users || [];
  const songsArr = songs || [];

  const todayKey = localDayKey(new Date());
  const yesterdayKey = prevDayKey(todayKey);

  const realOrders = ordersArr.filter((o) => !["unpaid", "expired", "rejected"].includes(o.status));
  const ordersTodayList = realOrders.filter((o) => localDayKey(o.created_at) === todayKey);
  const ordersToday = ordersTodayList.length;
  const ordPending = ordersTodayList.filter((o) => o.status === "pending").length;
  const ordBalance = ordersTodayList.filter((o) => o.status !== "pending" && Number(o.amount || 0) === 0).length;
  const ordPaid = ordersToday - ordPending - ordBalance;
  const ordersYest = realOrders.filter((o) => localDayKey(o.created_at) === yesterdayKey).length;
  // Точные счётчики из stats (список users — только первая страница на 20 строк).
  const usersToday = Number(k.users_today
    ?? usersArr.filter((u) => localDayKey(u.created_at) === todayKey).length);
  const usersYest = Number(k.users_yest
    ?? usersArr.filter((u) => localDayKey(u.created_at) === yesterdayKey).length);
  const inLastDays = (arr, days) => {
    const cut = Date.now() - days * 86400000;
    return arr.filter((x) => new Date(x.created_at).getTime() >= cut).length;
  };
  const ordersWeek = inLastDays(realOrders, 7);
  const usersWeek = inLastDays(usersArr, 7);
  const revenueToday = Number(m.today_revenue ?? m.revenue ?? 0);
  const revenueYest = Number(m.yesterday_revenue ?? 0);
  const songsToday = stats?.daily?.[stats.daily.length - 1]?.songs
    ?? songsArr.filter((s) => localDayKey(s.created_at) === todayKey).length;
  const songsYest = stats?.daily?.[stats.daily.length - 2]?.songs ?? 0;
  const songsWeek = (stats?.daily || []).slice(-7)
    .reduce((a, d) => a + (Number(d.songs) || 0), 0);
  const songsPeriod = period === "today"
    ? songsToday
    : (period === "yesterday" ? songsYest
       : period === "week" ? songsWeek
       : (k.total_gens ?? k.total_songs ?? 0));

  const isToday = period === "today";

  const kpis = [
    {
      testid: "kpi-new-orders", icon: ShoppingBag, color: BLUE, tint: "adm-kpi-blue",
      label: "Нових замовлень",
      value: isToday ? ordersToday
        : (period === "yesterday" ? ordersYest : period === "week" ? ordersWeek : realOrders.length),
      delta: deltaPct(ordersToday, ordersYest), muted: !isToday,
      sub: isToday
        ? `покупні ${ordPaid} · з балансу ${ordBalance} · в очікуванні ${ordPending} · вчора: ${ordersYest}`
        : `Сьогодні: ${ordersToday}`,
    },
    {
      testid: "kpi-new-users", icon: UserPlus, color: PURPLE, tint: "adm-kpi-purple",
      label: "Нових користувачів",
      value: isToday ? usersToday
        : (period === "yesterday" ? usersYest : period === "week" ? usersWeek
           : (usersTotal ?? k.total_users ?? 0)),
      delta: deltaPct(usersToday, usersYest), muted: !isToday,
      sub: isToday ? `Вчора: ${usersYest}` : `Сьогодні: ${usersToday}`,
    },
    {
      testid: "kpi-revenue", icon: Wallet, color: GREEN, tint: "adm-kpi-green",
      label: "Дохід",
      value: fmtMoney(isToday ? revenueToday : (k.total_revenue ?? m.revenue ?? 0)),
      delta: deltaPct(revenueToday, revenueYest), muted: !isToday,
      sub: (
        <span>
          <span className="text-[#6EE7B7] font-semibold">Чистий: {fmtMoney(isToday ? (m.net_today ?? 0) : (m.net_all ?? 0))}</span>
          <span className="text-[#475569]"> · дохід − генерації</span>
          {isToday && <span className="text-[#475569]"> · вчора: {fmtMoney(revenueYest)}</span>}
        </span>
      ),
    },
    {
      testid: "kpi-songs", icon: Music4, color: PINK, tint: "adm-kpi-pink",
      label: "Згенеровано пісень", value: songsPeriod,
      delta: deltaPct(songsToday, songsYest), muted: !isToday,
      sub: isToday ? `Вчора: ${songsYest}` : `Сьогодні: ${songsToday}`,
    },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[12px] text-[#64748B]">
          <span>Показники за період</span>
        </div>
        <div className="flex items-center gap-1 rounded-xl border border-white/[0.07] bg-[#0B1729] p-1">
          <button data-testid="period-today" onClick={() => setPeriod("today")}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${isToday ? "bg-[#17264A] text-white" : "text-[#64748B] hover:text-white"}`}>
            Сьогодні
          </button>
          <button data-testid="period-all" onClick={() => setPeriod("all")}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${!isToday ? "bg-[#17264A] text-white" : "text-[#64748B] hover:text-white"}`}>
            За весь час
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((kp) => <KpiCard key={kp.testid} {...kp} loading={loading} />)}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <RevenueCard orders={ordersArr} loading={loading} />
        <TrafficCard orders={ordersArr} />
        <CostsCard money={m} period={period} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <RecentOrdersCard orders={ordersArr} onTab={onTab} />
        <GenresCard stats={stats} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <GenStatusCard songs={songsArr} orders={ordersArr} />
        <PricingCard packages={packages} orders={ordersArr} />
      </div>

      <ExtraMetrics stats={stats} money={m} onModerate={onModerate} loading={loading} />
    </div>
  );
}
